import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { MagneticButton } from './MagneticButton';
import { ArrowRight, Handshake } from 'lucide-react';
import { AnimatedShinyText } from './ui/magic/AnimatedShinyText';
import { BorderBeam } from './ui/magic/BorderBeam';
import { AnimatedGridPattern } from './ui/magic/AnimatedGridPattern';

const heroWordVariants = {
  hidden: { opacity: 0, y: 18, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

export const HeroSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToFeatures = () => {
    const element = document.getElementById('features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#F6F8FC] pt-32 pb-8 md:pb-12">

      {/* Background: animated grid + soft brand glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <AnimatedGridPattern
          numSquares={32}
          maxOpacity={0.09}
          duration={2.2}
          className="text-[#003b82] stroke-slate-300/50 [mask-image:radial-gradient(760px_circle_at_50%_32%,white,transparent)]"
        />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[820px] h-[520px] bg-[#003b82]/[0.04] rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 w-full">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">

          {/* Badge - Cluely style */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <a href="/scan" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-800 text-sm font-semibold shadow-sm hover:shadow-md transition-shadow">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#003b82] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#003b82]"></span>
              </span>
              <AnimatedShinyText>New: Cura Scan Coming Soon</AnimatedShinyText>
              <ArrowRight className="w-3.5 h-3.5 text-[#003b82]" />
            </a>
          </motion.div>

          {/* Heading: word-by-word blur-in, aurora gradient on the last word */}
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
            }}
            className="text-5xl md:text-7xl text-slate-900 mb-6 leading-[1.1] tracking-wider"
          >
            {['Medical', 'Intelligence,'].map((word) => (
              <motion.span
                key={word}
                variants={heroWordVariants}
                className="inline-block mr-[0.22em] will-change-[transform,filter,opacity]"
              >
                {word}
              </motion.span>
            ))}
            <br />
            <motion.span
              variants={heroWordVariants}
              className="inline-block italic text-transparent bg-clip-text bg-gradient-to-r from-[#003b82] via-[#2f6cc4] to-[#003b82] gradient-animate px-[0.1em] pb-[0.18em] -mb-[0.18em] will-change-[transform,filter,opacity]"
            >
              Reimagined.
            </motion.span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-slate-700 font-medium max-w-2xl mb-10 leading-relaxed"
          >
            Cura partners with nonprofits and community organizations to bring instant medical answers
            and AI-powered symptom assessment to the seniors and caregivers they serve, via voice,
            chat, or WhatsApp, with no new apps to learn.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 mb-20"
          >
            <MagneticButton
              href="mailto:founders@mycura.org?subject=Partnership%20Inquiry"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#003b82] text-white font-medium hover:bg-[#002d62] transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 button-animate"
              strength={0.2}
            >
              <Handshake className="w-5 h-5 mr-2" />
              Partner With Us
            </MagneticButton>

            <MagneticButton
              onClick={scrollToFeatures}
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-slate-900 font-medium border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all shadow-sm hover:shadow-md hover:-translate-y-1"
              strength={0.2}
            >
              See How It Works
              <ArrowRight className="w-5 h-5 ml-2" />
            </MagneticButton>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-5xl mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200/60 bg-white ring-1 ring-slate-900/5">
              <img
                src="/Demo2.png"
                alt="Cura AI medical assistant interface showing symptom analysis and personalized health guidance for seniors"
                className="w-full h-auto"
                loading="lazy"
              />
              <BorderBeam size={250} duration={12} />
              <BorderBeam size={250} duration={12} delay={6} />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};
